/**
 * Sheets-side ledger-key construction + the per-sheet binding the stores
 * receive — slice **L2** (LR1) of the row-identity ledger design
 * (§3.1, §7). L1 landed the generic `Ledger<S>` in `@tento-core/ledger` with an
 * opaque branded `Ledger.Key`; this module is where the concrete
 * `data:` / `pref:` / `feed:` / `attr:` / `range` / `tz` union (§3.1) is
 * *constructed*. It is pulled forward from L3 because L2's version **bumps**
 * need keys — edges do NOT come in this slice.
 *
 * Keys are opaque downstream (`Ledger` never parses one, §3.4); these
 * constructors are the **only** place the serialized forms exist. The one
 * coupling: the `data:` serialized form is co-owned with `address-of.ts` (an
 * `Address` from `AddressOf` is already `data:`-prefixed) — `LedgerKeys.data`
 * is idempotent about the prefix so a canonical row's per-sheet address
 * (`data:<lookupKey>` from `AddressOf`) and the canonical store's raw-key bump
 * (`<lookupKey>` cast to `Address`) resolve to **one** node — the
 * canonical-share invariant (design §1.5 / M2).
 *
 * ## Scope binding is provisional (until L4)
 *
 * `SheetLedger = Ledger<NaiveDate.Range>` binds L1's scope type parameter to a
 * plain date range. The design's full scope is a **union** —
 * `Option<NaiveDate.Range>` *and/or a registered subtree* (§3.4) — so this
 * binding is provisional; L4 widens it to `NaiveDate.Range | SubtreeSpec` when
 * real edges (which carry scope) land. L2 never reads scope: it only bumps.
 *
 * ## Observability (`ledger/bumps`)
 *
 * `LedgerBumper` is the single write-side entry point — it wraps
 * `Ledger.bump` and, beside it (never on the hot path per-bump), keeps a
 * per-kind counter aggregate (the D6 pattern) with a pull-based `snapshot()`
 * for tests and one `Tracing.log("ledger/bumps", …)` per N-bump batch. The
 * channel measures write-side bump volume per key kind; `totalBumps()` also
 * pins the one-op-record-per-bump invariant against the TX0 recorder.
 */

import type { Option } from "../core/option.ts";
import type { NaiveDate } from "@tento-chrono";
import { DependencyAspect, type DependencyGraphReads } from "@tento-core/dependency-graph";
import { Ledger } from "@tento-core/ledger";
import { Tracing } from "@tento-core/tracing";

// `Address` is imported **type-only** — erased at runtime (`address-of` is an
// ids/ leaf; the variant classes sit behind `../rows/row-address.ts`, see the
// note below).
import type { Address } from "../ids/address-of.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";

// `../tx/tx-log` is a genuine LEAF (value-imports only `@tento-core/transaction-log`
// + `@tento-core/tracing`, never row-address / variants), so value-importing it
// here does NOT reopen the source-row TDZ cycle. `SheetTxOp` is a value (its
// `fromDetail` stamper); `SheetTxDetail` / `SheetTxRecorder` are type-only.
import { SheetTxOp, type SheetTxDetail, type SheetTxRecorder } from "../tx/tx-log.ts";

// NOTE: this module MUST NOT *value*-import `../rows/row-address` (or anything that
// pulls the variant classes). `../rows/variants/source-row.ts` imports `LedgerKeys`
// from here for
// its range bump; a dependency on `row-address` would close the cycle
// `source-row → ledger-keys → row-address → computed-data-source → source-row`
// and TDZ-crash on the `extends SourceRow` class evaluation. The address-aware
// half (`LedgerBinding`, which needs `RowAddress`) lives in `../render/ledger-binding`,
// which nothing in that variant chain imports.

/**
 * The sheets-side ledger instance. Scope bound to `NaiveDate.Range`
 * (provisional — widened to include `SubtreeSpec` at L4). One instance is
 * shared across every sheet an accessor opens (symmetric with the shared
 * `canonicalCells` store).
 */
export type SheetLedger = Ledger<NaiveDate.Range>;

/** The six `LedgerKey` kinds (design §3.1) — the counter aggregate's buckets. */
export type LedgerKeyKind = "data" | "attr" | "pref" | "feed" | "range" | "tz";

const DATA_PREFIX = "data:";

export namespace LedgerKeys {
  /**
   * The `data:` node for a row/cell data stream. Accepts either a full
   * `Address` from `AddressOf` (already `data:`-prefixed) or a raw data-name
   * body (a canonical lookup key cast to `Address`), normalizing to exactly one
   * `data:` prefix. This idempotence is what lands a canonical row's per-sheet
   * address and the canonical store's raw-key write on **one** node
   * (canonical-share, §1.5). Raw lookup keys start with `:`, so a body never
   * begins with `data:` — the normalization is unambiguous.
   */
  export function data(address: Address): Ledger.Key {
    const raw = address as string;
    return Ledger.key(raw.startsWith(DATA_PREFIX) ? raw : DATA_PREFIX + raw);
  }

  /** A source row's fetch-coverage consumer node — placement-keyed by `serial` (`LedgerSynthetic.coverageKey`). */
  export function coverage(serial: string): Ledger.Key {
    return Ledger.key(`coverage|${serial}`);
  }

  /** A row's attribute-bag node — placement-keyed by `serial` (§3.1). */
  export function attr(serial: string): Ledger.Key {
    return Ledger.key(`attr:${serial}`);
  }

  /** A calendar-preference node (`firstDay`, `timeFormat`, … kind string). */
  export function pref(kind: string): Ledger.Key {
    return Ledger.key(`pref:${kind}`);
  }

  /** A sync feed's settle-point node, keyed by its canonical key. */
  export function feed(canonicalKey: string): Ledger.Key {
    return Ledger.key(`feed:${canonicalKey}`);
  }

  /** The single viewport date-range node. */
  export const RANGE: Ledger.Key = Ledger.key("range");

  /** The single primary-timezone node. */
  export const TZ: Ledger.Key = Ledger.key("tz");
}

/** Flush a `ledger/bumps` breadcrumb every this-many bumps. */
const BUMP_FLUSH_BATCH = 128;

/**
 * The single write-side entry point onto a `SheetLedger`: `bump(key, facet,
 * kind)` performs the O(1) `Ledger.bump` and records the kind on the
 * `ledger/bumps` counter aggregate. One `LedgerBumper` per ledger is
 * shared across the canonical store, every sheet's stores, and the
 * preference/tz layers, so the counter is a single view of all bumps.
 */
export class LedgerBumper {
  // Per-kind bump counters (the D6 aggregate).
  readonly #counts: Record<LedgerKeyKind, number> = {
    data: 0,
    attr: 0,
    pref: 0,
    feed: 0,
    range: 0,
    tz: 0,
  };
  // Bumps since the last Tracing flush.
  #sinceFlush = 0;

  /**
   * TX0 — the transaction-log recorder, attached late (the `attachLedger` /
   * `attachRowLookup` precedent). `null` until an accessor wires it, so
   * `emptyStores` / standalone hydrate stay untouched and record nothing. Every
   * bump below records exactly one op when present — the one-op-record-per-bump
   * invariant (`recorder.recordCount === bumper.totalBumps()` over the attached
   * window).
   */
  #attachedRecorder: Option<SheetTxRecorder> = null;

  constructor(readonly ledger: SheetLedger) {}

  /** Attach the TX0 recorder. Idempotent replace; late-bound (dark in TX0). */
  attachRecorder(recorder: SheetTxRecorder): void {
    this.#attachedRecorder = recorder;
  }

  /** The attached recorder, or `null`. The reachability surface variants use
   *  (via `sheet.stores.sourceCellData.ledgerBumper?.recorder`). */
  get recorder(): Option<SheetTxRecorder> {
    return this.#attachedRecorder;
  }

  /**
   * Bump `(key, facet)` — the entire write-side contribution (LR1). Returns
   * the new monotonic version. `kind` feeds only the `ledger/bumps` counter; the
   * ledger itself never parses the key.
   *
   * Records the **minimal** `bump` fallback (preference / tz / un-migrated
   * write points) AFTER the increment so the record carries the post-bump `v`.
   */
  bump(key: Ledger.Key, facet: DependencyAspect, kind: LedgerKeyKind): number {
    const version = this.ledger.bump(key, facet);
    this.#record(kind);
    this.#attachedRecorder?.record({
      t: "bump",
      key: key as string,
      facet,
      bucket: kind,
      v: version,
    });
    return version;
  }

  /**
   * Bump `(key, facet)` and record an **enriched** op — the cell / attr / range
   * write points that hold their op detail. Record is stamped with the
   * post-bump `v` (`SheetTxOp.fromDetail`), preserving one-op-record-per-bump.
   */
  bumpOp(
    key: Ledger.Key,
    facet: DependencyAspect,
    kind: LedgerKeyKind,
    detail: SheetTxDetail,
  ): number {
    const version = this.ledger.bump(key, facet);
    this.#record(kind);
    this.#attachedRecorder?.record(SheetTxOp.fromDetail(detail, key as string, version));
    return version;
  }

  /**
   * Null-tolerant labeled scope for the variant bulk sites (TDZ-safe: variants
   * reach this via the bumper, never value-importing `tx-log`). Runs `fn`
   * inside `recorder.scope` when a recorder is attached; otherwise runs it
   * directly. See `tx-log.ts` header for why scoping rides the bumper.
   */
  txScope<T>(label: string, fn: () => T): T {
    const rec = this.#attachedRecorder;
    return rec != null ? rec.scope(label, fn) : fn();
  }

  /** Pull-based counter snapshot for tests. */
  bumpCounts(): Readonly<Record<LedgerKeyKind, number>> {
    return { ...this.#counts };
  }

  /** Total bumps across all kinds. */
  totalBumps(): number {
    let sum = 0;
    for (const k of Object.keys(this.#counts) as LedgerKeyKind[]) sum += this.#counts[k];
    return sum;
  }

  // Record a bump; batch-flush a breadcrumb.
  #record(kind: LedgerKeyKind): void {
    this.#counts[kind] += 1;
    this.#sinceFlush += 1;
    if (this.#sinceFlush >= BUMP_FLUSH_BATCH) this.#flush();
  }

  // `ledger/bumps`: cumulative per-kind bump counts, one Tracing.log per
  // N-bump batch (never per bump).
  #flush(): void {
    if (this.#sinceFlush === 0) return;
    this.#sinceFlush = 0;
    Tracing.log("ledger/bumps", { ...this.#counts });
  }
}

/**
 * What a `CellDataStore<K>` needs to bump its `data:` node: the shared
 * `LedgerBumper` plus a per-instantiation key mapper. The per-sheet store
 * (`K = RowLocalId`) maps through the sheet's `resolveAddress`; the
 * client-scoped canonical store (`K = string`) maps the raw lookup key
 * directly (`LedgerKeys.data(k as Address)`).
 */
export interface CellLedgerBump<K> {
  readonly bumper: LedgerBumper;
  bumpKeyFor(k: K): Ledger.Key;
}

/**
 * What a sheet's cell store reads of its ledger binding
 * (`render/ledger-binding.ts` `LedgerBinding`, which is one): the shared
 * bumper and the two address resolvers. Declared here, beside the keys, so
 * the store needs nothing of the binding's row classification
 * (`RowAddress`), which reaches the row variants.
 */
export interface CellLedgerBinding {
  readonly bumper: LedgerBumper;
  /** The row's data node for the write path (total). */
  resolveAddress(id: RowLocalId): Address;
  /** The row's output data node for the read/compare path; `null` for a placement-only row. */
  resolveOutputAddress(id: RowLocalId): Option<Address>;
}

/** The `data:`-node key mapper for a row-keyed store bound to `binding`. */
export function cellLedgerBump(binding: CellLedgerBinding): CellLedgerBump<RowLocalId> {
  return {
    bumper: binding.bumper,
    bumpKeyFor: (id: RowLocalId): Ledger.Key => LedgerKeys.data(binding.resolveAddress(id)),
  };
}

/**
 * The ledger shadow's seams as the cell store hands it out
 * (`render/ledger-shadow.ts` `LedgerShadow` is one, with `Row = ModelRow`):
 * a recompute seam mirrors a consumer's graph edges onto the ledger, and a
 * memoized recompute checks its signature against it.
 */
export interface LedgerShadowPort<Row> {
  /** Mirror `consumer`'s current graph edges onto the ledger (`LedgerShadow.declareFromGraph`). */
  declareFromGraph(graph: DependencyGraphReads<RowLocalId>, consumer: Row): void;
  /** Compare a memoized recompute's signature with the ledger's verdict, and record it (`LedgerShadow.checkAndRecord`). */
  checkAndRecord(consumer: Row, signature: string): void;
}

/**
 * The synthetic-edge declarer's re-expansion seam as the cell store hands it
 * out (`render/ledger-synthetic.ts` `LedgerSynthetic` is one, with
 * `Sheet = ModelSheet`).
 */
export interface LedgerSyntheticPort<Sheet> {
  onReexpand(sheet: Sheet, templateName: string): void;
}
